import {
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
// import Invoice from 'assets/common/Invoice.svg';
import InstaCard from 'coreComponents/InstaCard';
import dayjs from 'dayjs';
import { useLoadingStates } from 'hooks/useLoadingStates';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GetInvoiceDownloadLink } from 'services/api/paymentsApi';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchInvoices } from 'store/reducers/invoicesReducers';
import { RootState } from 'store/store';
import { Image } from 'styles';
import { TSpaceState, TTransaction } from 'typings';
import { downloadFile, formatPrice } from 'utils/commonFunctions';
import {
  APP_BUTTON_TYPES,
  isRunningInWebView,
  showAppButton
} from 'utils/webviewHelpers';
import { partnerAssets } from '../../partners/partnersAssets';

function BillingAndInvoiceSummary() {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'pages.billing'
  });

  const dispatch = useAppDispatch();

  const { spaceData, isLoading: isSpaceDataLoading } = useAppSelector(
    (state: RootState) => state.space as TSpaceState
  );

  const currentLocale = i18n.language;
  const {
    invoices,
    totalCount,
    isLoading: isInvoicesLoading
  } = useAppSelector((state) => state.invoices);

  const isLoading = useLoadingStates([isInvoicesLoading, isSpaceDataLoading]);

  useEffect(() => {
    if (isRunningInWebView()) {
      showAppButton(APP_BUTTON_TYPES.RELOAD, APP_BUTTON_TYPES.CLOSE);
    }
  }, [isRunningInWebView]);

  useEffect(() => {
    dispatch(fetchInvoices());
  }, []);

  const getInvoiceDownloadLink = async (transactionId: string) => {
    try {
      const res = await GetInvoiceDownloadLink(transactionId);

      downloadFile({
        url: res?.data?.invoice_link,
        filename: `invoice-${transactionId}.pdf`
      });
    } catch (err) {
      console.log('download error', err);
    }
  };

  if (isLoading) {
    return (
      <Stack gap="20px">
        <Skeleton
          variant="rounded"
          height={32}
          animation="pulse"
          sx={{ borderRadius: '8x' }}
        />
        <Skeleton
          variant="rounded"
          height={640}
          animation="pulse"
          sx={{ borderRadius: '20px' }}
        />
      </Stack>
    );
  }

  return (
    <>
      {spaceData?.id && !isLoading && (
        <div style={{ marginBottom: '20px' }}>
          <Typography variant="label2" marginBottom="16px" component="div">
            {t('transactionHistory')}
          </Typography>
          <TableContainer>
            {invoices.length > 0 && (
              <Table
                sx={{
                  minWidth: 650,
                  borderRadius: '20px',
                  background:
                    'linear-gradient(113deg,rgba(228, 228, 228, 1), rgba(241, 241, 241, 1)) padding-box, linear-gradient(356.14deg, #D5D5D5 9.39%, #FFFFFF 88.07%) border-box',
                  border: '1px solid transparent',
                  overflow: 'hidden'
                }}
              >
                <TableHead sx={{ border: 0 }}>
                  <TableRow
                    sx={{
                      background: (theme) => theme.palette.background.card
                    }}
                  >
                    <TableCell align="justify">
                      <Typography variant="title1">
                        {t('transaction')}
                      </Typography>
                    </TableCell>
                    <TableCell align="justify">
                      <Typography variant="title1">{t('status')}</Typography>
                    </TableCell>
                    <TableCell align="justify">
                      <Typography variant="title1">{t('type')}</Typography>
                    </TableCell>
                    <TableCell align="justify">
                      <Typography variant="title1">{t('amount')}</Typography>
                    </TableCell>
                    <TableCell align="justify">
                      <Typography variant="title1">
                        {t('transactionTime')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((transaction: TTransaction) => (
                    <TableRow
                      key={transaction.id}
                      sx={{
                        border: 0,
                        '&:nth-of-type(odd)': {
                          background: (theme) => theme.palette.background.dark
                        },
                        '&:nth-of-type(even)': {
                          background: (theme) => theme.palette.background.card
                        }
                      }}
                    >
                      <TableCell
                        sx={{
                          border: 0,
                          fontSize: (theme) => theme.typography.body2.fontSize,
                          fontWeight: (theme) =>
                            theme.typography.body2.fontWeight,
                          lineHeight: (theme) =>
                            theme.typography.body2.lineHeight
                        }}
                      >
                        {transaction.sku_name}
                      </TableCell>
                      <TableCell sx={{ border: 0 }} align="justify">
                        <Typography
                          variant="body2"
                          color={
                            transaction.status === 'Success'
                              ? 'tertiary.main'
                              : 'secondary.main'
                          }
                          fontWeight={500}
                        >
                          {transaction.status}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="justify"
                        sx={{
                          border: 0,
                          fontSize: (theme) => theme.typography.body2.fontSize,
                          fontWeight: (theme) =>
                            theme.typography.body2.fontWeight,
                          lineHeight: (theme) =>
                            theme.typography.body2.lineHeight
                        }}
                        onClick={() => getInvoiceDownloadLink(transaction.id)}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            background: (theme) => theme.palette.primary.main,
                            display: 'flex',
                            padding: '2px 8px'
                          }}
                        >
                          <Image
                            src={partnerAssets.invoiceIcon}
                            height="16"
                            width="16"
                            style={{ marginRight: '6px' }}
                          />
                          <Typography variant="body2" color="text.contrastText">
                            {t('invoice')}
                          </Typography>
                        </Button>
                      </TableCell>
                      <TableCell align="justify" sx={{ border: 0 }}>
                        {/* country is US as we need upto 2 decimal points */}
                        <Typography variant="body2">
                          {formatPrice(
                            transaction?.amount,
                            transaction?.currency,
                            'US',
                            currentLocale
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="justify" sx={{ border: 0 }}>
                        <Typography variant="body2">
                          {dayjs(transaction.transaction_time).format(
                            'YYYY.MM.DD HH:mm:ss'
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          {invoices && totalCount === 0 && (
            <Stack justifyContent="center" alignItems="center">
              <Typography variant="body2" sx={{ cursor: 'auto' }}>
                {t('noTransactions')}
              </Typography>
            </Stack>
          )}
        </div>
      )}

      {!spaceData?.id && !isLoading && (
        <InstaCard>
          <Stack alignItems="center">
            <Typography variant="label2">{t('noSpaceCreated')}</Typography>
          </Stack>
        </InstaCard>
      )}
    </>
  );
}

export default BillingAndInvoiceSummary;
